import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  ArrowLeft, Car, Gauge, Calendar, DollarSign, Wrench, Clock,
  CheckCircle, AlertTriangle, AlertCircle, ChevronRight,
  Droplet, Circle, Octagon, Wind, Battery, Fan, FileText, Shield,
  Plus, RefreshCw, Settings, Image, ShieldCheck, ShieldPlus, Receipt,
  MapPin, Star, Camera
} from 'lucide-react';
import API from '@/utils/api';
import { toast } from 'sonner';
import GlobalHeader from '@/components/GlobalHeader';
import OverviewTab from './tabs/OverviewTab';
import MaintenanceTabNew from './tabs/MaintenanceTabNew';
import ReceiptsTabNew from './tabs/ReceiptsTabNew';
import ProtectionTab from './tabs/ProtectionTab';
import QuickActionMenu from './QuickActionMenu';
import UpdateMaintenanceDialog from './UpdateMaintenanceDialog';
import LogMaintenanceDialog from './LogMaintenanceDialog';
import AddReceiptDialog from './AddReceiptDialog';
import AddProtectionDialog from './AddProtectionDialog';

// Theme colors - pastel teal
const THEME = {
  primary: '#BFE3E0',
  light: '#D5EFEC',
  lighter: '#EAF7F6',
  dark: '#1F5F5B',
  icon: '#8ECFC9',
  surface: '#F5FAFA',
  border: '#B5DDD9',
  healthy: '#22C55E',
  attention: '#F59E0B',
  urgent: '#EF4444',
  unknown: '#94A3B8',
};

// Status color mapping
const STATUS_COLORS = {
  healthy: { bg: 'bg-green-50', border: 'border-green-200', icon: 'text-green-600', iconBg: 'bg-green-100' },
  good: { bg: 'bg-green-50', border: 'border-green-200', icon: 'text-green-600', iconBg: 'bg-green-100' },
  attention_soon: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-600', iconBg: 'bg-amber-100' },
  due_soon: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-600', iconBg: 'bg-amber-100' },
  urgent: { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-600', iconBg: 'bg-red-100' },
  overdue: { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-600', iconBg: 'bg-red-100' },
  setup_needed: { bg: 'bg-slate-50', border: 'border-slate-200', icon: 'text-slate-500', iconBg: 'bg-slate-100' },
  unknown: { bg: 'bg-slate-50', border: 'border-slate-200', icon: 'text-slate-500', iconBg: 'bg-slate-100' },
};

const STATUS_ICONS = {
  healthy: CheckCircle,
  good: CheckCircle,
  attention_soon: Clock,
  due_soon: Clock,
  urgent: AlertCircle,
  overdue: AlertCircle,
  setup_needed: Settings,
  unknown: Settings,
};

const VehicleDetailView = ({ vehicle, onBack, onRefresh }) => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showQuickActions, setShowQuickActions] = useState(false);
  
  // Dialog states
  const [selectedMaintenanceItem, setSelectedMaintenanceItem] = useState(null);
  const [showMaintenanceDialog, setShowMaintenanceDialog] = useState(false);
  const [showLogDialog, setShowLogDialog] = useState(false);
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [showProtectionDialog, setShowProtectionDialog] = useState(false);
  const [protectionType, setProtectionType] = useState(null);

  const fetchHealthData = useCallback(async () => {
    try {
      const response = await API.get(`/vehicles/${vehicle.id}/health`);
      setHealthData(response.data);
    } catch (error) {
      console.error('Failed to fetch health data:', error);
      toast.error('Failed to load vehicle data');
    } finally {
      setLoading(false);
    }
  }, [vehicle.id]);

  useEffect(() => {
    fetchHealthData();
  }, [fetchHealthData]);

  const handleRefresh = async () => {
    setLoading(true);
    await fetchHealthData();
    onRefresh?.();
    toast.success('Data refreshed');
  };

  // Quick action handlers
  const handleLogService = (item = null) => {
    setSelectedMaintenanceItem(item);
    setShowLogDialog(true);
    setShowQuickActions(false);
  };

  const handleUpdateMaintenance = (item) => {
    setSelectedMaintenanceItem(item);
    setShowMaintenanceDialog(true);
  };

  const handleAddReceipt = () => {
    setShowReceiptDialog(true);
    setShowQuickActions(false);
  };

  const handleAddProtection = (type = null) => {
    setProtectionType(type);
    setShowProtectionDialog(true);
    setShowQuickActions(false);
  };

  // Submit handlers
  const handleMaintenanceSubmit = async (data) => {
    try {
      await API.post(`/vehicles/${vehicle.id}/maintenance-tracking`, {
        vehicle_id: vehicle.id,
        maintenance_type: selectedMaintenanceItem.type,
        ...data
      });
      toast.success('Tracking updated — one less thing to remember');
      setShowMaintenanceDialog(false);
      setSelectedMaintenanceItem(null);
      fetchHealthData();
    } catch (error) {
      toast.error('Failed to update maintenance info');
    }
  };

  const handleLogSubmit = async (data) => {
    try {
      await API.post(`/vehicles/${vehicle.id}/log-maintenance`, {
        service_type: selectedMaintenanceItem?.type || data.service_type,
        ...data
      });
      toast.success('Nice work! Service logged successfully');
      setShowLogDialog(false);
      setSelectedMaintenanceItem(null);
      fetchHealthData();
    } catch (error) {
      toast.error('Failed to log service');
    }
  };

  const handleReceiptSubmit = async (data) => {
    try {
      await API.post('/receipts', {
        vehicle_id: vehicle.id,
        ...data
      });
      toast.success('Receipt saved — your records are up to date');
      setShowReceiptDialog(false);
      fetchHealthData();
    } catch (error) {
      toast.error('Failed to add receipt');
    }
  };

  const handleProtectionSubmit = async (data) => {
    try {
      await API.post('/protections', {
        vehicle_id: vehicle.id,
        ...data
      });
      toast.success('Protection added — your vehicle is covered');
      setShowProtectionDialog(false);
      setProtectionType(null);
      fetchHealthData();
    } catch (error) {
      toast.error('Failed to add protection');
    }
  };

  const handleUploadPhoto = () => {
    // Create hidden file input and trigger it
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);
        
        await API.post(`/vehicles/${vehicle.id}/photo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        toast.success('Photo uploaded — looking great!');
        fetchHealthData();
        onRefresh?.();
      } catch (error) {
        console.error('Upload failed:', error);
        toast.error('Failed to upload photo');
      }
    };
    input.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: THEME.lighter }}>
        <GlobalHeader
          icon={Car}
          iconBgColor={THEME.primary}
          iconColor="text-teal-700"
          title="Loading..."
          onBack={onBack}
        />
        <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-32 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </main>
      </div>
    );
  }

  const statusColors = STATUS_COLORS[healthData?.overall_status] || STATUS_COLORS.unknown;
  const StatusIcon = STATUS_ICONS[healthData?.overall_status] || Settings;

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: THEME.lighter }}>
      <GlobalHeader
        icon={Car}
        iconBgColor={THEME.primary}
        iconColor="text-teal-700"
        title={healthData?.vehicle?.nickname || healthData?.vehicle?.name || 'Vehicle'}
        subtitle={healthData?.vehicle?.nickname ? healthData?.vehicle?.name : null}
        onBack={onBack}
        onRefresh={handleRefresh}
      />

      <main className="max-w-4xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        {/* Tab Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList 
            className="grid w-full grid-cols-4 mb-4 p-1 h-auto rounded-xl"
            style={{ backgroundColor: THEME.light }}
          >
            {[
              { value: 'overview', label: 'Overview', icon: Car },
              { value: 'maintenance', label: 'Maintenance', icon: Wrench },
              { value: 'receipts', label: 'Receipts', icon: Receipt },
              { value: 'protection', label: 'Protection', icon: Shield },
            ].map(({ value, label, icon: TabIcon }) => (
              <TabsTrigger
                key={value}
                value={value}
                className={`flex flex-col sm:flex-row items-center gap-1 py-2.5 px-1 sm:px-3 rounded-lg text-xs sm:text-sm font-medium transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm`}
                style={{ 
                  color: activeTab === value ? THEME.dark : '#64748B'
                }}
                data-testid={`tab-${value}`}
              >
                <TabIcon className="h-4 w-4" />
                <span>{label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-0">
            <OverviewTab 
              healthData={healthData}
              statusColors={statusColors}
              StatusIcon={StatusIcon}
              onLogService={handleLogService}
              onAddReceipt={handleAddReceipt}
              onItemClick={handleUpdateMaintenance}
              onUploadPhoto={handleUploadPhoto}
              theme={THEME}
            />
          </TabsContent>

          {/* Maintenance Tab */}
          <TabsContent value="maintenance" className="mt-0">
            <MaintenanceTabNew
              healthData={healthData}
              vehicleId={vehicle.id}
              onItemClick={handleUpdateMaintenance}
              onLogService={handleLogService}
              onRefresh={fetchHealthData}
              theme={THEME}
            />
          </TabsContent>

          {/* Receipts Tab */}
          <TabsContent value="receipts" className="mt-0">
            <ReceiptsTabNew
              vehicleId={vehicle.id}
              healthData={healthData}
              onAddReceipt={handleAddReceipt}
              onRefresh={fetchHealthData}
              theme={THEME}
            />
          </TabsContent>

          {/* Protection Tab */}
          <TabsContent value="protection" className="mt-0">
            <ProtectionTab
              vehicleId={vehicle.id}
              healthData={healthData}
              onAddProtection={handleAddProtection}
              onRefresh={fetchHealthData}
              theme={THEME}
            />
          </TabsContent>
        </Tabs>
      </main>

      {/* Floating Quick Action Button */}
      <QuickActionMenu
        open={showQuickActions}
        onOpenChange={setShowQuickActions}
        onLogService={() => handleLogService()}
        onAddReceipt={handleAddReceipt}
        onAddProtection={handleAddProtection}
        theme={THEME}
      />

      {/* Dialogs */}
      {selectedMaintenanceItem && showMaintenanceDialog && (
        <UpdateMaintenanceDialog
          open={showMaintenanceDialog}
          onClose={() => {
            setShowMaintenanceDialog(false);
            setSelectedMaintenanceItem(null);
          }}
          item={selectedMaintenanceItem}
          currentMileage={healthData?.vehicle?.current_mileage}
          onSubmit={handleMaintenanceSubmit}
          onLogMaintenance={() => {
            setShowMaintenanceDialog(false);
            setShowLogDialog(true);
          }}
        />
      )}

      {showLogDialog && (
        <LogMaintenanceDialog
          open={showLogDialog}
          onClose={() => {
            setShowLogDialog(false);
            setSelectedMaintenanceItem(null);
          }}
          item={selectedMaintenanceItem}
          currentMileage={healthData?.vehicle?.current_mileage}
          maintenanceItems={healthData?.maintenance_items}
          onSubmit={handleLogSubmit}
        />
      )}

      {showReceiptDialog && (
        <AddReceiptDialog
          open={showReceiptDialog}
          onClose={() => setShowReceiptDialog(false)}
          vehicleId={vehicle.id}
          currentMileage={healthData?.vehicle?.current_mileage}
          onSubmit={handleReceiptSubmit}
        />
      )}

      {showProtectionDialog && (
        <AddProtectionDialog
          open={showProtectionDialog}
          onClose={() => {
            setShowProtectionDialog(false);
            setProtectionType(null);
          }}
          vehicleId={vehicle.id}
          initialType={protectionType}
          onSubmit={handleProtectionSubmit}
        />
      )}
    </div>
  );
};

export default VehicleDetailView;
